import React, { useState } from 'react'
import { Link } from 'react-router-dom'
import { motion } from 'framer-motion'
import {
  Video,
  Camera,
  Calendar,
  Users,
  Mic,
  Play,
  CheckCircle,
  ArrowRight,
  Star,
  Clock,
  Zap,
  Heart,
  Sparkles,
} from 'lucide-react'

const Services = () => {
  const [activeService, setActiveService] = useState(0)

  const services = [
    {
      id: 'event-management',
      icon: Calendar,
      title: 'Event Management',
      subtitle: 'Complete Event Planning & Coordination',
      description: 'From concept to execution, we handle every aspect of your event to ensure a seamless and memorable experience.',
      features: [
        'Event Planning & Design',
        'Venue Selection & Setup',
        'Vendor Coordination',
        'Timeline Management',
        'On-site Coordination',
        'Post-event Follow-up',
      ],

      duration: '2-6 months planning',
      color: 'from-primary-500 to-primary-600',
      image: '/services/event-management.jpg',
    },
    {
      id: 'video-production',
      icon: Video,
      title: 'Video Production',
      subtitle: 'Professional Video Creation',
      description: 'High-quality video production services using state-of-the-art equipment and creative storytelling techniques.',
      features: [
        '4K Video Recording',
        'Multi-camera Setup',
        'Professional Lighting',
        'Drone Videography',
        'Post-production Editing',
        'Color Grading & Effects',
      ],

      duration: '1-3 weeks delivery',
      color: 'from-secondary-500 to-secondary-600',
      image: '/services/video-production.jpg',
    },
    {
      id: 'corporate-events',
      icon: Users,
      title: 'Corporate Events',
      subtitle: 'Professional Business Events',
      description: 'Sophisticated corporate event solutions that enhance your brand image and engage your audience effectively.',
      features: [
        'Conference Production',
        'Product Launches',
        'Team Building Events',
        'Award Ceremonies',
        'Corporate Meetings',
        'Brand Activations',
      ],

      duration: '1-4 months planning',
      color: 'from-accent-500 to-accent-600',
      image: '/services/corporate-events.jpg',
    },
    {
      id: 'photography',
      icon: Camera,
      title: 'Photography',
      subtitle: 'Professional Photography Services',
      description: 'Capture stunning moments with our professional photography services, from portraits to event coverage.',
      features: [
        'Event Photography',
        'Portrait Sessions',
        'Product Photography',
        'Architectural Photography',
        'Photo Editing & Retouching',
        'Digital Gallery Delivery',
      ],

      duration: '1-2 weeks delivery',
      color: 'from-purple-500 to-purple-600',
      image: '/services/photography.jpg',
    },
    {
      id: 'live-streaming',
      icon: Mic,
      title: 'Live Streaming',
      subtitle: 'Professional Live Broadcasting',
      description: 'Reach a global audience with our professional live streaming services for events, conferences, and special occasions.',
      features: [
        'Multi-platform Streaming',
        'HD Quality Broadcasting',
        'Interactive Features',
        'Real-time Monitoring',
        'Technical Support',
        'Recording & Archive',
      ],

      duration: 'Real-time service',
      color: 'from-pink-500 to-pink-600',
      image: '/services/live-streaming.jpg',
    },
    {
      id: 'wedding-coverage',
      icon: Heart,
      title: 'Wedding Coverage',
      subtitle: 'Complete Wedding Documentation',
      description: 'Comprehensive wedding coverage that captures every precious moment of your special day with artistic excellence.',
      features: [
        'Ceremony Coverage',
        'Reception Documentation',
        'Bridal Portraits',
        'Highlight Reel Creation',
        'Photo & Video Packages',
        'Same-day Editing',
      ],

      duration: '2-4 weeks delivery',
      color: 'from-indigo-500 to-indigo-600',
      image: '/services/wedding-coverage.jpg',
    },
  ]

  const packages = [
    {
      name: 'Essential',
      description: 'Perfect for small events and basic coverage',
      features: [
        'Up to 4 hours coverage',
        'Single camera setup',
        'Basic editing',
        'Digital delivery',
        'Email support',
      ],
      popular: false,
    },
    {
      name: 'Professional',
      description: 'Ideal for medium-sized events and businesses',
      features: [
        'Up to 8 hours coverage',
        'Multi-camera setup',
        'Advanced editing',
        'Same-day highlights',
        'Priority support',
        'Drone footage included',
      ],
      popular: true,
    },
    {
      name: 'Premium',
      description: 'Complete solution for large events and corporations',
      features: [
        'Full day coverage',
        'Multiple camera angles',
        'Professional editing suite',
        'Live streaming included',
        '24/7 support',
        'Custom graphics & effects',
        'Dedicated project manager',
      ],
      popular: false,
    },
  ]

  const addOns = [
    { name: 'Drone Footage', icon: Zap },
    { name: 'Same-day Editing', icon: Clock },
    { name: 'Additional Camera', icon: Video },
    { name: 'Live Streaming', icon: Mic },
    { name: 'Photo Booth', icon: Camera },
    { name: 'Custom Graphics', icon: Sparkles },
  ]

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="min-h-screen pt-24"
    >
      {/* Hero Section */}
      <section className="section-padding">
        <div className="container-custom">
          <motion.div
            initial={{ y: 50, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            className="text-center mb-16"
          >
            <h1 className="text-4xl md:text-6xl font-bold text-white mb-6">
              Our <span className="gradient-text">Services</span>
            </h1>
            <p className="text-xl md:text-2xl text-white/70 max-w-4xl mx-auto leading-relaxed">
              Comprehensive video production and event management solutions tailored to bring your vision to life with professional excellence.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Services Grid */}
      <section className="section-padding bg-black/20">
        <div className="container-custom">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {services.map((service, index) => {
              const Icon = service.icon
              return (
                <motion.div
                  key={service.id}
                  initial={{ y: 50, opacity: 0 }}
                  whileInView={{ y: 0, opacity: 1 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                  className="glass-card hover-lift hover-glow group cursor-pointer"
                  onClick={() => setActiveService(index)}
                >
                  <div className={`w-16 h-16 rounded-2xl bg-gradient-to-r ${service.color} flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-300`}>
                    <Icon className="w-8 h-8 text-white" />
                  </div>
                  
                  <h3 className="text-xl font-semibold text-white mb-2 group-hover:text-primary-300 transition-colors duration-300">
                    {service.title}
                  </h3>
                  
                  <p className="text-primary-300 font-medium mb-4">
                    {service.subtitle}
                  </p>
                  
                  <p className="text-white/70 leading-relaxed mb-6">
                    {service.description}
                  </p>
                  
                  <div className="space-y-2 mb-6">
                    {service.features.slice(0, 3).map((feature, featureIndex) => (
                      <div key={featureIndex} className="flex items-center space-x-2">
                        <CheckCircle className="w-4 h-4 text-primary-400 flex-shrink-0" />
                        <span className="text-white/80 text-sm">{feature}</span>
                      </div>
                    ))}
                    {service.features.length > 3 && (
                      <div className="text-primary-300 text-sm font-medium">
                        +{service.features.length - 3} more features
                      </div>
                    )}
                  </div>
                  
                  <div className="flex items-center justify-between pt-4 border-t border-white/20">
                    <div>
                      <div className="text-white/60 text-sm">{service.duration}</div>
                    </div>
                    <ArrowRight className="w-5 h-5 text-primary-400 group-hover:translate-x-1 transition-transform duration-300" />
                  </div>
                </motion.div>
              )
            })}
          </div>
        </div>
      </section>

      {/* Service Details Modal/Section */}
      <section className="section-padding">
        <div className="container-custom">
          <motion.div
            key={activeService}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="glass-card"
          >
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
              <div className="space-y-6">
                <div className="flex items-center space-x-4">
                  <div className={`p-4 rounded-2xl bg-gradient-to-r ${services[activeService].color}`}>
                    {React.createElement(services[activeService].icon, { className: 'w-8 h-8 text-white' })}
                  </div>
                  <div>
                    <h2 className="text-3xl font-bold text-white">
                      {services[activeService].title}
                    </h2>
                    <p className="text-primary-300 font-medium">
                      {services[activeService].subtitle}
                    </p>
                  </div>
                </div>
                
                <p className="text-white/80 text-lg leading-relaxed">
                  {services[activeService].description}
                </p>
                
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  {services[activeService].features.map((feature, index) => (
                    <div key={index} className="flex items-center space-x-3">
                      <CheckCircle className="w-5 h-5 text-primary-400 flex-shrink-0" />
                      <span className="text-white/80">{feature}</span>
                    </div>
                  ))}
                </div>
                
                <div className="flex flex-col sm:flex-row items-start sm:items-center space-y-4 sm:space-y-0 sm:space-x-8 pt-6 border-t border-white/20">
                  <div>
                    <div className="text-white/60">
                      {services[activeService].duration}
                    </div>
                  </div>
                  
                  <div className="flex space-x-4">
                    <Link
                      to="/booking"
                      className="bg-gradient-to-r from-primary-500 to-secondary-500 rounded-full px-6 py-3 font-medium transition-all duration-300 hover:scale-105 active:scale-95 shadow-lg"
                    >
                      Book Now
                    </Link>
                    <Link
                      to="/contact"
                      className="glass-button"
                    >
                      Get Quote
                    </Link>
                  </div>
                </div>
              </div>
              
              <div className="relative">
                <div className="aspect-video rounded-2xl bg-gradient-to-br from-primary-500/20 to-secondary-500/20 flex items-center justify-center">
                  <Play className="w-16 h-16 text-white/60" />
                  <div className="absolute inset-0 bg-black/20 rounded-2xl" />
                </div>
                <div className="absolute -bottom-4 -right-4 w-24 h-24 bg-accent-500/20 rounded-full blur-xl floating" />
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Packages */}
      <section className="section-padding bg-black/20">
        <div className="container-custom">
          <motion.div
            initial={{ y: 50, opacity: 0 }}
            whileInView={{ y: 0, opacity: 1 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">
              Service <span className="gradient-text">Packages</span>
            </h2>
            <p className="text-xl text-white/70 max-w-3xl mx-auto">
              Choose the perfect package for your needs, or let us create a custom solution just for you.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {packages.map((pkg, index) => (
              <motion.div
                key={index}
                initial={{ y: 50, opacity: 0 }}
                whileInView={{ y: 0, opacity: 1 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className={`glass-card relative hover-lift ${
                  pkg.popular ? 'ring-2 ring-primary-400/50' : ''
                }`}
              >
                {pkg.popular && (
                  <div className="absolute -top-4 left-1/2 transform -translate-x-1/2">
                    <div className="bg-gradient-to-r from-primary-500 to-secondary-500 rounded-full px-4 py-2 text-sm font-medium flex items-center space-x-2">
                      <Star className="w-4 h-4" />
                      <span>Most Popular</span>
                    </div>
                  </div>
                )}
                
                <div className="text-center mb-8">
                  <h3 className="text-2xl font-bold text-white mb-2">{pkg.name}</h3>
                  <p className="text-white/70">{pkg.description}</p>
                </div>
                
                <div className="space-y-4 mb-8">
                  {pkg.features.map((feature, featureIndex) => (
                    <div key={featureIndex} className="flex items-center space-x-3">
                      <CheckCircle className="w-5 h-5 text-primary-400 flex-shrink-0" />
                      <span className="text-white/80">{feature}</span>
                    </div>
                  ))}
                </div>
                
                <Link
                  to="/booking"
                  className={`block w-full text-center py-3 rounded-full font-medium transition-all duration-300 hover:scale-105 active:scale-95 ${
                    pkg.popular
                      ? 'bg-gradient-to-r from-primary-500 to-secondary-500 text-white shadow-lg'
                      : 'glass-button'
                  }`}
                >
                  Choose {pkg.name}
                </Link>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Add-ons */}
      <section className="section-padding">
        <div className="container-custom">
          <motion.div
            initial={{ y: 50, opacity: 0 }}
            whileInView={{ y: 0, opacity: 1 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">
              Add-on <span className="gradient-text">Services</span>
            </h2>
            <p className="text-xl text-white/70 max-w-3xl mx-auto">
              Enhance your package with additional services to create the perfect solution for your event.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {addOns.map((addon, index) => {
              const Icon = addon.icon
              return (
                <motion.div
                  key={index}
                  initial={{ y: 30, opacity: 0 }}
                  whileInView={{ y: 0, opacity: 1 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                  className="glass-card hover-lift group cursor-pointer"
                >
                  <div className="flex items-center space-x-4">
                    <div className="p-3 rounded-xl bg-gradient-to-r from-primary-500 to-secondary-500 group-hover:scale-110 transition-transform duration-300">
                      <Icon className="w-6 h-6 text-white" />
                    </div>
                    <div className="flex-1">
                      <h3 className="text-lg font-semibold text-white group-hover:text-primary-300 transition-colors duration-300">
                        {addon.name}
                      </h3>
                    </div>
                  </div>
                </motion.div>
              )
            })}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="section-padding bg-black/20">
        <div className="container-custom">
          <motion.div
            initial={{ y: 50, opacity: 0 }}
            whileInView={{ y: 0, opacity: 1 }}
            viewport={{ once: true }}
            className="glass-card text-center space-y-8 relative overflow-hidden"
          >
            <div className="absolute inset-0 bg-gradient-to-r from-primary-500/10 via-secondary-500/10 to-accent-500/10" />
            
            <div className="relative z-10">
              <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
                Ready to Get <span className="gradient-text">Started</span>?
              </h2>
              <p className="text-xl text-white/70 max-w-2xl mx-auto mb-8">
                Let's discuss your project and create a custom solution that perfectly fits your needs and budget.
              </p>
              
              <div className="flex flex-col sm:flex-row items-center justify-center space-y-4 sm:space-y-0 sm:space-x-6">
                <Link
                  to="/booking"
                  className="bg-gradient-to-r from-primary-500 to-secondary-500 rounded-full px-8 py-4 text-lg font-semibold transition-all duration-300 hover:scale-105 active:scale-95 shadow-xl hover:shadow-2xl flex items-center space-x-2 group"
                >
                  <Calendar className="w-5 h-5" />
                  <span>Book Consultation</span>
                  <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform duration-300" />
                </Link>
                
                <Link
                  to="/contact"
                  className="glass-button flex items-center space-x-2 text-lg group"
                >
                  <span>Get Custom Quote</span>
                </Link>
              </div>
            </div>
          </motion.div>
        </div>
      </section>
    </motion.div>
  )
}

export default Services